import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";

const ClientFilterContext = createContext({
  clientId: null,
  client: null,
  clients: [],
  setClientId: () => {},
  clear: () => {},
});

/**
 * Global "opdrachtgever" filter shared across Dashboard, Rapportages, Projects, Weekstaat, and the app header.
 * Reads the initial preference from the authenticated user's profile (dashboard_client_id) and persists any
 * change back to the server so it's restored on the next login.
 */
export function ClientFilterProvider({ children }) {
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clientId, setClientIdState] = useState(user?.dashboard_client_id || null);
  const [clients, setClients] = useState([]);

  // Sync state when auth context loads / updates
  useEffect(() => {
    setClientIdState(user?.dashboard_client_id || null);
  }, [user?.dashboard_client_id]);

  // Load opdrachtgever list once (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/clients?kind=opdrachtgever`)
      .then((r) => setClients(r.data || []))
      .catch((err) => console.error("Opdrachtgeverslijst laden mislukt:", err));
  }, [isAdmin]);

  const setClientId = useCallback(async (id) => {
    const cleaned = id && id !== "__all__" ? id : null;
    setClientIdState(cleaned); // optimistic
    if (isAdmin) {
      try {
        await axios.put(`${API}/users/me/dashboard-client`, { client_id: cleaned });
        refresh?.();
      } catch (err) { console.error("Dashboard-opdrachtgever opslaan mislukt:", err); }
    }
  }, [isAdmin, refresh]);

  const clear = useCallback(() => setClientId(null), [setClientId]);

  const client = useMemo(() => clients.find((c) => c.id === clientId) || null, [clients, clientId]);

  const value = useMemo(
    () => ({ clientId, client, clients, setClientId, clear }),
    [clientId, client, clients, setClientId, clear]
  );

  return <ClientFilterContext.Provider value={value}>{children}</ClientFilterContext.Provider>;
}

export const useClientFilter = () => useContext(ClientFilterContext);
